import React, { useCallback } from "react";
import ReactFlow, {
  useNodesState,
  useEdgesState,
  addEdge,
  MiniMap,
  Controls,
} from "reactflow";
import "reactflow/dist/style.css";
import Hub from "./components/Hub/Hub";
import NodeEl from "./components/NodeEl/NodeEl";


const nodeTypes = {
  nodeEl: NodeEl,
  hubEl: Hub,
};
 
const initialNodes: any = [
  {
    id: "risk-universe",
    sourcePosition: "right",
    type: "hubEl",
    data: {
      risks: 320,
      controls: 300,
      inherentRisk: 57,
      residualRisk: 26,
    },
    position: { x: -300, y: -100 },
  },
  {
    id: "financial-node",
    type: "nodeEl",
    data: {
      title: "Financial",
      status: "success",
      risks: 12,
      controls: 5,
    },
    position: { x: 320, y: -320 },
  },
  {
    id: "operations-node",
    type: "nodeEl",
    data: {
      title: "Operations",
      status: "error",
      risks: 19,
      controls: 8,
    },
    position: { x: 320, y: -160 },
  },
  {
    id: "regulatory-node",
    type: "nodeEl",
    data: {
      title: "Regulatory",
      status: "error",
      risks: 18,
      controls: 5,
    },
    position: { x: 320, y: 0 },
  },
  {
    id: "fraud-node",
    type: "nodeEl",
    data: {
      title: "Fraud",
      status: "info",
      risks: 23,
      controls: 17,
    },
    position: { x: 320, y: 160 },
  },
  {
    id: "strategic-node",
    type: "nodeEl",
    data: {
      title: "Strategic",
      risks: 23,
      controls: 17,
    },
    position: { x: 320, y: 320 },
  },

  {
    id: "internal-Controls",
    type: "nodeEl",
    data: {
      title: "Internal Controls",
      risks: 2,
    },
    position: { x: 900, y: -650 },
  },
  {
    id: "budgetPlaning",
    type: "nodeEl",
    data: {
      title: "Budget Planning",
      risks: 3,
    },
    position: { x: 900, y: -550 },
  },
  {
    id: "financialReporting",
    type: "nodeEl",
    data: {
      title: "Financial Reporting",
      risks: 4,
    },
    position: { x: 900, y: -450 },
  },
  {
    id: "financialLosses",
    type: "nodeEl",
    data: {
      title: "Financial Losses",
      risks: 6,
    },
    position: { x: 900, y: -350 },
  },
  {
    id: "supply-chain-node",
    type: "nodeEl",
    data: {
      title: "Supply Chain",
      status: "error",
      risks: 23,
    },
    position: { x: 900, y: -200 },
  },
  {
    id: "hazards-node",
    type: "nodeEl",
    data: {
      title: "Hazards",
      risks: 23,
    },
    position: { x: 900, y: -100 },
  },
  {
    id: "physicalAssets-node",
    type: "nodeEl",
    data: {
      title: "Physical Assets",
      risks: 8,
    },
    position: { x: 900, y: 0 },
  },
  //
  {
    id: "sanctions-node",
    type: "nodeEl",
    data: {
      title: "Sanctions",
      status: "error",
      risks: 2,
    },
    position: { x: 900, y: 150 },
  },
  {
    id: "legal-node",
    type: "nodeEl",
    data: {
      title: "Legal",
      risks: 8,
    },
    position: { x: 900, y: 250 },
  },
  {
    id: "physical-assets-node",
    type: "nodeEl",
    data: {
      title: "Physical Assets",
      risks: 8,
    },
    position: { x: 900, y: 350 },
  },
];

// 
const initialEdges = [
  {
    id: "horizontal-e1-2",
    source: "risk-universe",
    target: "financial-node",
  },
  {
    id: "horizontal-e1-3",
    source: "risk-universe",
    target: "operations-node",
  },

  {
    id: "horizontal-e1-4",
    source: "risk-universe",
    target: "regulatory-node",
  },
  {
    id: "horizontal-e3-5",
    source: "risk-universe",
    target: "fraud-node",
  },
  {
    id: "horizontal-e3-6",
    source: "risk-universe",
    target: "strategic-node",
  },
  {
    id: "internal-Controls-e1",
    source: "financial-node",
    target: "internal-Controls",
  },
  {
    id: "budgetPlaning-e1",
    source: "financial-node",
    target: "budgetPlaning",
  },
  {
    id: "financialReporting-e1",
    source: "financial-node",
    target: "financialReporting",
  },
  {
    id: "financialLosses-e1",
    source: "financial-node",
    target: "financialLosses",
  },
  {
    id: "horizontal-e5-7",
    source: "operations-node",
    target: "supply-chain-node",
  },
  {
    id: "horizontal-e6-8",
    source: "operations-node",
    target: "hazards-node",
  },
  {
    id: "physicalAssets-ed",
    source: "operations-node",
    target: "physicalAssets-node",
  },

  //
  {
    id: "sanctions-ed",
    source: "regulatory-node",
    target: "sanctions-node",
  },
  {
    id: "legal-ed",
    source: "regulatory-node",
    target: "legal-node",
  },
  {
    id: "physical-assets-ed",
    source: "regulatory-node",
    target: "physical-assets-node",
  },
];

interface FlowChartProps {
  isMiniMap?: boolean;
  isControls?: boolean;
}

export default function FlowChart({ isMiniMap , isControls }: FlowChartProps) {
  const [nodes, _, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const onConnect = useCallback(
    (params: any) => setEdges((els) => addEdge(params, els)),
    []
  );

  return (
    <div style={{ width: "100%", height: "calc(100vh - 12rem)" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        fitView
        attributionPosition="bottom-left"
      >
        {isMiniMap && <MiniMap />}
        {isControls && <Controls position="top-right" />}
      </ReactFlow>
    </div>
  );
}
